/**
 * Capacitor native background video upload (@capgo/capacitor-uploader).
 *
 * Reliable large-video uploads need the client→S3 byte transfer to survive
 * the Capacitor WebView being suspended (app backgrounded/locked) and
 * network changes (Wi-Fi <-> cellular) mid-transfer. Uploads run as
 * JavaScript in the WebView, so iOS pauses them on background — this plugin
 * hands the PUT to iOS's native background `URLSession` (Android: WorkManager
 * foreground service), which keeps going while the app is suspended.
 *
 * Mirrors the `capacitor/push_registration.js` bridge pattern: lazy memoized
 * dynamic `import()` (clear-on-failure) so the plugin is never downloaded or
 * parsed outside the Capacitor shell, guards on
 * `isCapacitorEnvironment() && isCapacitorPluginAvailable("Uploader")`
 * ("Uploader" is the plugin's registerPlugin() name — see its
 * dist/esm/index.js), a single `'events'` listener registered once that fans
 * out to per-upload promise resolvers keyed by the `id` `startUpload()`
 * returns, and `reportError -> Honeybadger.notify(..,
 * {context:{component:"capacitor_background_upload"}})`.
 *
 * This is the ONLY plugin evaluated that meets every hard requirement: raw
 * binary PUT to a presigned S3 URL, custom headers passed verbatim, iOS
 * background URLSession + Android WorkManager, and an events channel we can
 * correlate by task id. If it is ever unavailable (old app build, plugin
 * failed to load, flag off), `isBackgroundUploadAvailable()` returns false
 * and callers fall back to the existing JS upload paths — see
 * `components/shared/videos/upload_video.js`.
 */

import Honeybadger from "@honeybadger-io/js";
import {
  isCapacitorEnvironment,
  isCapacitorPluginAvailable,
} from "./detection";

// The plugin's registerPlugin() name (dist/esm/index.js:
// `registerPlugin('Uploader', ...)`), NOT the npm package name. The JS gate
// hard-depends on this string matching exactly what the native shell
// registers, or isCapacitorPluginAvailable silently disables the feature.
const UPLOADER_PLUGIN_NAME = "Uploader";

// iOS background URLSession uploads must read from a `file://` path — it
// cannot upload an in-memory Blob. So the native branch first materializes
// the picked File to disk via @capacitor/filesystem, which means reading it
// (in bounded chunks — see materializeNativeTempFile) and base64-encoding it
// in JS (writeFile/appendFile only accept base64/UTF8 strings from the JS
// side, not raw binary). RISK (launch blocker per the background-upload
// plan): keep this ceiling conservative until proven on real devices — files
// above it fall back to the existing chunked JS upload path instead of
// attempting native.
export const NATIVE_UPLOAD_MAX_BYTES = 150 * 1024 * 1024; // 150MB

// How long to wait for a terminal ('completed'/'failed') event for a given
// upload id before giving up. Generous on purpose — the entire point of this
// module is surviving long backgrounding, so this is NOT a transfer-speed
// timeout, only a dead-man's switch for a genuinely lost/dropped native event
// (e.g. the app was force-quit and relaunched, losing this JS module's
// in-memory pendingUploads entry, or the native side failed to emit a
// terminal event at all). Without this, a dropped event hangs the caller's
// promise forever with no error ever surfaced.
const NATIVE_UPLOAD_EVENT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Cached import promises — resolved once, reused thereafter. Cleared on
// failure so a later call can retry (e.g. a transient dynamic-import failure
// on a flaky connection).
let _uploaderImport = null;
let _filesystemImport = null;

function getUploader() {
  if (!_uploaderImport) {
    _uploaderImport = import("@capgo/capacitor-uploader")
      .then((mod) => mod.Uploader)
      .catch((error) => {
        _uploaderImport = null; // allow retry on next call
        throw error;
      });
  }
  return _uploaderImport;
}

function getFilesystem() {
  if (!_filesystemImport) {
    _filesystemImport = import("@capacitor/filesystem")
      .then((mod) => ({ Filesystem: mod.Filesystem, Directory: mod.Directory }))
      .catch((error) => {
        _filesystemImport = null; // allow retry on next call
        throw error;
      });
  }
  return _filesystemImport;
}

/**
 * Report a background-upload failure so it's visible in monitoring.
 * Reporting must never itself throw.
 */
function reportError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_background_upload" },
    });
  } catch {
    // no-op
  }
}

/**
 * Whether the native background-upload plugin is available in the current
 * shell. False outside Capacitor, or if the native side never registered
 * the "Uploader" plugin (e.g. an app build predating the native shell work).
 * @returns {boolean}
 */
export function isBackgroundUploadAvailable() {
  return (
    isCapacitorEnvironment() && isCapacitorPluginAvailable(UPLOADER_PLUGIN_NAME)
  );
}

// Per-upload-id resolvers for in-flight `startUpload()` calls, fanned out to
// from the single `'events'` listener below.
const pendingUploads = new Map();

function acknowledge(Uploader, eventId) {
  // Progress ('uploading') events carry no eventId and need no ack. Completed
  // / failed events are cached natively for redelivery across app restarts —
  // ack removes them from that cache once handled, whether or not this JS
  // session still has a pending resolver for the id (e.g. after a cold
  // restart the old resolver is gone, but the stale cached event must still
  // be cleared or it re-fires forever on every future plugin init).
  if (!eventId) return;
  Uploader.acknowledgeEvent({ eventId }).catch(() => {
    // Best-effort — a failed ack just means the event may redeliver once more.
  });
}

function handleUploadEvent(Uploader, event) {
  const pending = pendingUploads.get(event.id);

  if (event.name === "uploading") {
    pending?.onProgress?.(event.payload?.percent ?? 0);
    return;
  }

  if (event.name === "completed") {
    pendingUploads.delete(event.id);
    const statusCode = event.payload?.statusCode;
    if (
      typeof statusCode === "number" &&
      statusCode >= 200 &&
      statusCode < 300
    ) {
      pending?.resolve({ statusCode });
    } else {
      const error = new Error(
        `Native background upload completed with non-2xx status ${statusCode}`,
      );
      error.statusCode = statusCode;
      pending?.reject(error);
    }
    acknowledge(Uploader, event.eventId);
    return;
  }

  if (event.name === "failed") {
    pendingUploads.delete(event.id);
    const error = new Error(
      event.payload?.error || "Native background upload failed",
    );
    const statusCode = event.payload?.statusCode;
    error.statusCode = statusCode;
    // A 'failed' event (as opposed to a non-2xx 'completed') means the
    // native side never got an HTTP response at all — connection lost,
    // DNS failure, cellular handoff, etc. There is no statusCode to classify
    // by, but this is exactly the transient-network class of failure the
    // caller's retry loop must retry (that's the whole reliability point of
    // this feature). Mark it explicitly so callers don't have to guess from
    // an absent statusCode.
    if (typeof statusCode !== "number") {
      error.nativeTransportFailure = true;
    }
    pending?.reject(error);
    acknowledge(Uploader, event.eventId);
  }
}

// Memoized 'events' listener registration promise. Awaited by
// nativeBackgroundPut (not fire-and-forget) so a registration failure
// rejects the caller's upload instead of leaving it hanging forever waiting
// for events that can never arrive.
let listenerPromise = null;

/**
 * Register the single 'events' listener, once. Must be called with an
 * already-resolved Uploader plugin reference (see getUploader()).
 * @returns {Promise<void>}
 */
function ensureEventListener(Uploader) {
  if (!listenerPromise) {
    listenerPromise = Uploader.addListener("events", (event) =>
      handleUploadEvent(Uploader, event),
    )
      .then(() => undefined)
      .catch((error) => {
        listenerPromise = null; // allow a later call to retry
        reportError(error);
        throw error;
      });
  }
  return listenerPromise;
}

/**
 * Start a native background PUT of a file already materialized on disk
 * (`fileUri`) to a presigned URL. Resolves once the native session reports
 * `completed` with a 2xx status; rejects on `failed`, any non-2xx
 * `completed`, or if no terminal event arrives within
 * NATIVE_UPLOAD_EVENT_TIMEOUT_MS. The upload continues even if the WebView is
 * torn down or backgrounded mid-transfer — that's the entire point of this
 * module.
 *
 * @param {object} args
 * @param {string} args.fileUri - `file://` path to the bytes to upload.
 * @param {string} args.url - presigned destination URL (e.g. S3 PUT URL).
 * @param {Object<string,string>} args.headers - headers to send verbatim.
 * @param {string} [args.mimeType] - content type of the file.
 * @param {(percent:number)=>void} [args.onProgress] - 0-100 progress callback.
 * @returns {Promise<{statusCode:number}>}
 */
export async function nativeBackgroundPut({
  fileUri,
  url,
  headers,
  mimeType,
  onProgress,
}) {
  const Uploader = await getUploader();
  // Propagates a registration failure as a rejection instead of silently
  // leaving the caller's promise (constructed below) waiting forever.
  await ensureEventListener(Uploader);

  const { id } = await Uploader.startUpload({
    filePath: fileUri,
    serverUrl: url,
    method: "PUT",
    headers,
    mimeType,
    uploadType: "binary",
    // Retry policy is fully owned by the JS caller (upload_video.js), which
    // re-presigns a fresh URL on every attempt. Letting the plugin ALSO
    // retry internally would stack attempts against a URL that only the
    // outer loop refreshes, so leave this at 0.
    maxRetries: 0,
    notificationTitle: "Uploading video…",
  });

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingUploads.delete(id);
      const error = new Error(
        "Native background upload timed out waiting for a terminal event",
      );
      // Same shape as a 'failed' event with no statusCode (handleUploadEvent
      // above) — no HTTP response was ever received here either, just a
      // dropped/lost terminal event instead of a lost connection. Tag it
      // identically so upload_video.js's isRetriableNativeUploadError treats
      // it as the same transient, retriable class instead of a hard failure
      // (without this it has no statusCode, no axios .code/.response, and its
      // message doesn't contain the literal substring "timeout" that
      // isRetriableUploadError's string match looks for — "timed out" — so it
      // would otherwise fall through as non-retriable).
      error.nativeTransportFailure = true;
      reject(error);
    }, NATIVE_UPLOAD_EVENT_TIMEOUT_MS);

    const settle = (fn) => (value) => {
      clearTimeout(timeoutId);
      fn(value);
    };

    pendingUploads.set(id, {
      resolve: settle(resolve),
      reject: settle(reject),
      onProgress,
    });
  });
}

// Base64-encode an ArrayBuffer in fixed-size chunks (avoids blowing the
// String.fromCharCode.apply argument-count limit on large buffers).
const BASE64_CHUNK_BYTES = 0x8000; // 32KB
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_BYTES) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(offset, offset + BASE64_CHUNK_BYTES),
    );
  }
  return btoa(binary);
}

// Read + base64-encode the file in bounded chunks rather than materializing
// one giant in-memory base64 string. A single-shot buffer -> binary-string ->
// base64-string pass on a 150MB file (this module's own size ceiling) would
// hold roughly 3x the file size in memory simultaneously (~500MB) — exactly
// the OOM risk NATIVE_UPLOAD_MAX_BYTES exists to bound. Chunking keeps peak
// memory to a small multiple of TEMP_FILE_CHUNK_BYTES regardless of file
// size. Mirrors the existing chunked-read pattern in upload_video.js's
// getFileMD5Base64.
const TEMP_FILE_CHUNK_BYTES = 4 * 1024 * 1024; // 4MB

/**
 * Materialize a picked File to a temp file on disk so the native background
 * session has a `file://` path to read from. Reads the file in bounded
 * chunks — same de-stale intent as `utils/prepare_image_for_upload.js`
 * (copying bytes into memory now makes the upload immune to the OS
 * invalidating the original File handle later, e.g. on backgrounding), but
 * chunked to bound peak memory. Caller is responsible for calling
 * `releaseNativeTempFile()` once the upload settles.
 *
 * @param {File} file
 * @returns {Promise<{uri:string, path:string}>}
 */
export async function materializeNativeTempFile(file) {
  const { Filesystem, Directory } = await getFilesystem();
  // Directory.LibraryNoCloud rather than Directory.Cache: Cache is explicitly
  // documented as OS-purgeable under disk pressure at any time, which would
  // undermine the exact reliability guarantee this feature exists to provide
  // (surviving a long-running, possibly backgrounded, transfer).
  // LibraryNoCloud persists for the app's lifetime and is excluded from
  // iCloud backup (this is transient upload scratch data, not user content
  // worth backing up).
  const directory = Directory.LibraryNoCloud;
  // Computed up front (not derived from anything the write loop produces) so
  // it's known even if a chunk write below throws before this function ever
  // returns — see the catch block's cleanup.
  const path = `tkc-native-uploads/${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;

  try {
    let offset = 0;
    do {
      const end = Math.min(offset + TEMP_FILE_CHUNK_BYTES, file.size);
      // Sequential by design: each write must land before the next chunk is
      // read, bounding memory to one chunk in flight rather than the whole file.
      const chunk = file.slice(offset, end);
      const buffer = await chunk.arrayBuffer();
      const base64 = arrayBufferToBase64(buffer);
      if (offset === 0) {
        await Filesystem.writeFile({
          path,
          data: base64,
          directory,
          recursive: true,
        });
      } else {
        await Filesystem.appendFile({ path, data: base64, directory });
      }
      offset = end;
    } while (offset < file.size);
  } catch (error) {
    // A chunk write/append throwing here (e.g. disk full, OS killed the
    // write) means this function never returns `path` to the caller, so the
    // caller's `finally releaseNativeTempFile(tempFilePath)` never runs —
    // tempFilePath is only assigned from this function's successful return
    // value (see upload_video.js's uploadNativeBackground). Without this,
    // whatever bytes did land at `path` leak permanently in
    // Directory.LibraryNoCloud, which — unlike Cache — is NOT OS-purgeable.
    // Best-effort delete before re-throwing; deleteFile itself failing (e.g.
    // writeFile threw before creating anything) must not mask the original
    // error.
    try {
      await Filesystem.deleteFile({ path, directory });
    } catch {
      // No-op — nothing to clean up, or the delete lost a race. Either way
      // the original error is what the caller needs to see.
    }
    throw error;
  }

  const { uri } = await Filesystem.getUri({ path, directory });
  return { uri, path };
}

/**
 * Best-effort delete of a temp file written by materializeNativeTempFile().
 * Never throws — a leaked temp file is not itself dangerous and must never
 * mask or block the caller's upload outcome.
 * @param {string|null|undefined} path
 */
export async function releaseNativeTempFile(path) {
  if (!path) return;
  try {
    const { Filesystem, Directory } = await getFilesystem();
    await Filesystem.deleteFile({
      path,
      directory: Directory.LibraryNoCloud,
    });
  } catch (error) {
    reportError(error);
  }
}

export default {
  isBackgroundUploadAvailable,
  nativeBackgroundPut,
  materializeNativeTempFile,
  releaseNativeTempFile,
  NATIVE_UPLOAD_MAX_BYTES,
};
